import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { EstadoObraBadge, EstadoRedesBadge } from '@/components/obras/estado-obra-badge';
import { RDT_ETIQUETA_ABREVIADA } from '@/components/obras/constants';
import { PlacaCc } from '@/components/obras/placa-cc';
import { CodigoObraCopiable } from '@/components/obras/codigo-obra-copiable';
import { fFecha, fHora, fmtMonto, fmtMontoCorto } from '@/components/rdt/rdt-domain';
import type { ObraDetalle } from '@/types/api';

function hoyMedianoche(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

interface Props {
  obra: ObraDetalle;
}

// Header de DetalleObra — misma placa de CC + badge de estado que la card de
// origen (obra-card.tsx), más la línea de metadatos del contrato (MVP §5.2):
// semáforo de vencimiento, fin previsto, oferta compacta en millones, equipo
// completo en tooltip sobre el coordinador y quién está a cargo de las RDT.
//
// R3-C (plan_migracion_reui.md): EstadoObraBadge / EstadoRedesBadge (R0,
// sobre reui/badge) en vez de los chips a mano; Tooltip de ui/ en vez de
// `title` nativo (Ajuste 13); el volver = Button con render={<Link/>} y
// nativeButton={false} (punto 29 del plan).
export function HeaderObra({ obra }: Props) {
  // Hallazgo 2 (QA v0.17.0): si se entró desde el Avivador (irARede en
  // use-avivador-estado.ts pasa `state: { from: 'avivador' }`), «Volver»
  // tiene que llevar de vuelta ahí y no a la home de Obras.
  const location = useLocation();
  const desdeAvivador = (location.state as { from?: string } | null)?.from === 'avivador';
  const volverA = desdeAvivador ? '/avivador' : '/';
  const volverLabel = desdeAvivador ? 'Volver al Avivador' : 'Volver a Obras';

  const fechaFin = obra.contrato.fechaFin;
  const dias = fechaFin
    ? Math.round((new Date(`${fechaFin}T00:00:00`).getTime() - hoyMedianoche().getTime()) / 86_400_000)
    : null;
  const semaforoTip =
    dias === null ? '' : dias < 0 ? `vencido hace ${-dias} días` : `faltan ${dias} días`;
  const finRojo = dias !== null && dias < 60;

  const equipoTip = `Jefatura de obra: ${obra.jefeDeObra ?? '—'}\nAdministración de obra: ${obra.admin ?? '—'}`;

  return (
    <header className="flex-none border-b border-border-subtle bg-surface-page px-[26px] pb-[15px] pt-5">
      <div className="flex min-w-0 items-center gap-[11px]">
        <Tooltip>
          <TooltipTrigger
            render={
              <Button
                variant="ghost"
                size="icon-sm"
                render={<Link to={volverA} />}
                nativeButton={false}
                aria-label={volverLabel}
                className="flex-none"
              />
            }
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          </TooltipTrigger>
          <TooltipContent>{volverLabel}</TooltipContent>
        </Tooltip>

        <PlacaCc cc={obra.cc} color={obra.color} colorTexto={obra.colorTexto} size="lg" />

        <h1 className="m-0 min-w-0 truncate text-[19px] font-[650] tracking-[-0.015em] text-foreground">{obra.denominacion}</h1>

        <EstadoObraBadge estado={obra.estado ?? 'Sin estado'} className="flex-none" />

        {obra.estadoRdt && (
          // U-10 (rc.6, qa_visual_reui_2026-09-28.md): este chip nunca
          // había migrado `RDT_ETIQUETA_ABREVIADA` (vivía solo en
          // obra-card.tsx) — «Esperando Definitiva» se mostraba entero acá
          // aunque la card de Obras ya lo abrevia a «Esp. Definitiva». El
          // texto completo sigue accesible: `EstadoRedesBadge` lo pone en un
          // `sr-only` interno (sin `title=` nativo, guarda sin-title.test.ts)
          // y, para el mouse, se agrega también al Tooltip existente cuando
          // no hay «último cambio» que ya lo mencione.
          //
          // «RDT» ADENTRO de la píldora, como la rc.1 (V2, 28/9: decisión de
          // Juan — antes era un <b> suelto AFUERA del Badge).
          <Tooltip>
            <TooltipTrigger
              render={<span tabIndex={0} className="inline-flex flex-none items-center" />}
            >
              <EstadoRedesBadge estado={obra.estadoRdt} etiqueta={RDT_ETIQUETA_ABREVIADA[obra.estadoRdt]} prefijo="RDT" />
            </TooltipTrigger>
            <TooltipContent className="whitespace-pre-line">
              {obra.ultimoCambioEstadoRedes
                ? `${obra.ultimoCambioEstadoRedes.despues} · antes ${obra.ultimoCambioEstadoRedes.antes ?? '—'} · ${obra.ultimoCambioEstadoRedes.usuarioNombre} · ${fHora(obra.ultimoCambioEstadoRedes.creadoAt)}${obra.ultimoCambioEstadoRedes.nota ? `\n${obra.ultimoCambioEstadoRedes.nota}` : ''}`
                : `${obra.estadoRdt} · sin cambios desde la app`}
            </TooltipContent>
          </Tooltip>
        )}

        {/* La acción «Estado de redes» NO vive acá (Juan, 17/9, v0.15.2: «sacalo
            de la page de la obra, queda mal; solo de la page principal») — se
            cambia desde la home en modo archivo (switch «Archivadas»). El chip
            RDT de arriba conserva el tooltip con el último cambio. */}
        {obra.logoUrl &&
          (obra.cliente ? (
            <Tooltip>
              <TooltipTrigger
                render={
                  <span className="ml-auto inline-flex h-[26px] max-w-[110px] flex-none items-center rounded-[7px] border border-border-subtle bg-white px-[7px]" />
                }
              >
                <img src={obra.logoUrl} alt="" className="h-[17px] w-auto max-w-[84px] object-contain" />
              </TooltipTrigger>
              <TooltipContent>{obra.cliente}</TooltipContent>
            </Tooltip>
          ) : (
            <span className="ml-auto inline-flex h-[26px] max-w-[110px] flex-none items-center rounded-[7px] border border-border-subtle bg-white px-[7px]">
              <img src={obra.logoUrl} alt="" className="h-[17px] w-auto max-w-[84px] object-contain" />
            </span>
          ))}
      </div>

      <div className="mt-[9px] flex flex-wrap items-center gap-x-[7px] gap-y-[5px] pl-[41px] text-[12px] text-muted-foreground">
        {/* Código de obra con copiar, primer dato de la línea (Juan, 29/9: no había cómo copiarlo desde la obra). El
            ícono de copiar aparece en el lugar de la etiqueta «Código» (con mouse, al pasar por el dato). */}
        {obra.codigoObra && (
          <CodigoObraCopiable etiqueta="Código" codigo={obra.codigoObra} textoClassName="max-w-[40ch] text-[11px]" />
        )}
        {obra.codigoObra && (obra.contrato.fechaInicio || obra.contrato.fechaFin) && (
          <span className="text-border-strong">·</span>
        )}
        {(obra.contrato.fechaInicio || obra.contrato.fechaFin) && (
          <span>
            <span className="text-text-tertiary">Contrato</span> {fFecha(obra.contrato.fechaInicio) ?? '—'} →{' '}
            {finRojo ? <b className="text-destructive">{fFecha(obra.contrato.fechaFin) ?? '—'}</b> : (fFecha(obra.contrato.fechaFin) ?? '—')}
            {dias !== null && (
              <Tooltip>
                <TooltipTrigger
                  render={<span tabIndex={0} aria-label={semaforoTip} className="ml-1.5 inline-flex items-center align-middle" />}
                >
                  <span
                    aria-hidden="true"
                    className={cn(
                      'inline-block h-[7px] w-[7px] rounded-full',
                      dias < 60 ? 'bg-destructive' : dias < 90 ? 'bg-warning opacity-85' : 'bg-success opacity-55',
                    )}
                  />
                </TooltipTrigger>
                <TooltipContent>{semaforoTip}</TooltipContent>
              </Tooltip>
            )}
          </span>
        )}
        {obra.contrato.fechaPrevistaFin && (
          <>
            <span className="text-border-strong">·</span>
            <span>
              <span className="text-text-tertiary">Fin previsto</span> {fFecha(obra.contrato.fechaPrevistaFin)}
            </span>
          </>
        )}
        {obra.contrato.oferta != null && (
          <>
            <span className="text-border-strong">·</span>
            <Tooltip>
              <TooltipTrigger render={<span />}>
                <span className="text-text-tertiary">Oferta</span> {fmtMontoCorto(obra.contrato.oferta)}
              </TooltipTrigger>
              <TooltipContent>{fmtMonto.format(obra.contrato.oferta)}</TooltipContent>
            </Tooltip>
          </>
        )}
        {obra.coordinador && (
          <>
            <span className="text-border-strong">·</span>
            <span>
              <span className="text-text-tertiary">Coordinación</span>{' '}
              <Tooltip>
                <TooltipTrigger
                  render={
                    <span
                      tabIndex={0}
                      className="cursor-help underline decoration-dotted decoration-border-strong underline-offset-[3px]"
                    />
                  }
                >
                  {obra.coordinador}
                </TooltipTrigger>
                <TooltipContent className="whitespace-pre-line">{equipoTip}</TooltipContent>
              </Tooltip>
            </span>
          </>
        )}
        <span className="text-border-strong">·</span>
        {obra.encargada ? (
          <span>
            <span className="text-text-tertiary">Control RDT</span> {obra.encargada}
          </span>
        ) : (
          <span className="text-text-tertiary">Sin Control RDT asignado</span>
        )}
      </div>
    </header>
  );
}
